/**
 * The email a person typed on cleancleaver.com/get/ (the page a PDF's QR opens; apps/landing/site/get.js). The page opens sign-up
 * as /app/?signup=1&src=pdf-<doc>#email=… : the email rides in the address FRAGMENT, which a browser never sends to a server, so it is
 * in no log and no referrer. We take it off the address at once, keep it for this tab only (sessionStorage), and the email sign-in
 * starts with it already typed: the person types their email once. `src` is only a document type and is not kept.
 */
export const APP_EMAIL_KEY = "cc.app.email";

function safeSession(): Storage | undefined {
  try { return typeof sessionStorage === "undefined" ? undefined : sessionStorage; } catch { return undefined; }
}
const EMAIL_RE = /^[^\s@<>(),;:"\\[\]]+@[^\s@<>(),;:"\\[\]]+\.[A-Za-z]{2,}$/;
let memory: string | null = null; // the same email for this page load when storage is blocked

/** "#email=you%40x.com" -> the email, or null. Anything that does not look like one address is ignored. */
export function emailFromHash(hash: string): string | null {
  const v = new URLSearchParams(hash.replace(/^#/, "")).get("email")?.trim().toLowerCase() ?? "";
  return v.length <= 254 && EMAIL_RE.test(v) && !v.includes("..") ? v : null;
}

/** Keeps the email from the address fragment. Returns it (so the caller can take the fragment off the address), or null. */
export function captureAppEmail(hash: string, storage: Storage | undefined = safeSession()): string | null {
  const e = emailFromHash(hash);
  if (!e) return null;
  memory = e;
  try { storage?.setItem(APP_EMAIL_KEY, e); } catch { /* blocked storage: it lives for this page load */ }
  return e;
}
export function storedAppEmail(storage: Storage | undefined = safeSession()): string | null {
  try { const v = storage?.getItem(APP_EMAIL_KEY); if (v && EMAIL_RE.test(v)) return v; } catch { /* fall through */ }
  return memory;
}
export function clearAppEmail(storage: Storage | undefined = safeSession()): void {
  memory = null;
  try { storage?.removeItem(APP_EMAIL_KEY); } catch { /* ignore */ }
}
